import React, { useEffect, useRef } from 'react';
import { User, AtSign } from 'lucide-react';

interface User {
  id: string;
  email: string;
  user_name: string;
}

interface MentionDropdownProps {
  users: User[];
  selectedIndex: number;
  onSelectUser: (user: User) => void;
  onSetSelectedIndex: (index: number) => void;
  isLoading: boolean;
  searchText: string;
}

export const MentionDropdown: React.FC<MentionDropdownProps> = ({
  users,
  selectedIndex,
  onSelectUser,
  onSetSelectedIndex,
  isLoading,
  searchText,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Auto-scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && optionRefs.current[selectedIndex]) {
      optionRefs.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex]);

  // Reset refs array when users change
  useEffect(() => {
    optionRefs.current = new Array(users.length).fill(null);
  }, [users.length]);

  const highlightMatch = (text: string, search: string) => {
    if (!search) return text;
    
    const regex = new RegExp(`(${search})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 font-semibold">
          {part}
        </span>
      ) : part
    );
  };

  if (isLoading) {
    return (
      <div 
        ref={dropdownRef}
        className="absolute z-50 w-64 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto"
      >
        <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
          <AtSign className="h-4 w-4" />
          Đang tải users...
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div 
        ref={dropdownRef}
        className="absolute z-50 w-64 mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
      >
        <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
          <AtSign className="h-4 w-4" />
          {searchText ? `Không tìm thấy user cho "${searchText}"` : 'Nhập tên hoặc email để tìm user...'}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={dropdownRef}
      className="absolute z-50 w-80 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto"
    >
      <div className="px-3 py-2 text-xs text-gray-600 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
        <AtSign className="h-3 w-3" />
        Chọn user để mention
      </div>
      
      {users.map((user, index) => (
        <button
          key={user.id}
          ref={(el) => optionRefs.current[index] = el}
          type="button"
          onClick={() => onSelectUser(user)}
          className={`w-full px-3 py-2 text-left text-sm flex items-center gap-3 transition-colors ${
            index === selectedIndex 
              ? 'bg-blue-100 text-blue-900' 
              : 'hover:bg-gray-100'
          }`}
          onMouseEnter={() => onSetSelectedIndex(index)}
          onMouseLeave={() => onSetSelectedIndex(-1)}
        >
          <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">
              {highlightMatch(user.user_name || user.email, searchText)}
            </div>
            {user.user_name && (
              <div className="text-xs text-gray-500 truncate">
                {highlightMatch(user.email, searchText)}
              </div>
            )}
          </div>
          <div className="text-xs text-gray-400">
            @{user.user_name || user.email}
          </div>
        </button>
      ))}
      
      <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t border-gray-200">
        Dùng ↑↓ để chọn, Enter để confirm, Esc để hủy
      </div>
    </div>
  );
};